import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styles from "../styles/SearchPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faUser,
  faProjectDiagram,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import { getSearchResults } from "../api/api";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [filterUsers, setFilterUsers] = useState(
    searchParams.get("users") !== "false"
  ); // Default: true
  const [filterWorkflows, setFilterWorkflows] = useState(
    searchParams.get("workflows") !== "false"
  ); // Default: true

  const [allUsers, setAllUsers] = useState([]);
  const [allWorkflows, setAllWorkflows] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchResults = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    setLoading(true);

    const response = await getSearchResults(searchQuery);
    setAllUsers(response.users);
    setAllWorkflows(response.files);
    setFilteredUsers(filterUsers ? response.users : []);
    setFilteredWorkflows(filterWorkflows ? response.files : []);
    setLoading(false);
  };

  useEffect(() => {
    if (query.trim()) {
      fetchResults(query);
    }
  }, [query]);

  useEffect(() => {
    const newQuery = searchParams.get("q") || "";
    if (newQuery !== query) {
      setQuery(newQuery);
    }
  }, [searchParams]);

  const handleSearch = (e) => {
    setQuery(e.target.value);
    setSearchParams({
      q: e.target.value,
      users: filterUsers,
      workflows: filterWorkflows,
    });
  };

  const toggleFilterUsers = () => {
    setFilterUsers((prev) => {
      const newFilter = !prev;
      setFilteredUsers(newFilter ? allUsers : []);
      setSearchParams({
        q: query,
        users: newFilter,
        workflows: filterWorkflows,
      });
      return newFilter;
    });
  };

  const toggleFilterWorkflows = () => {
    setFilterWorkflows((prev) => {
      const newFilter = !prev;
      setFilteredWorkflows(newFilter ? allWorkflows : []);
      setSearchParams({ q: query, users: filterUsers, workflows: newFilter });
      return newFilter;
    });
  };

  const handleUserClick = (userId) => {
    navigate(`/u/${userId}`);
  };

  const handleWorkflowClick = (workflowId) => {
    navigate(`/workflow/${workflowId}`);
  };

  return (
    <div className={styles.searchPage}>
      {/* Left Column - Filters */}
      <div className={styles.leftColumn}>
        <h3>
          <FontAwesomeIcon icon={faFilter} /> Filters
        </h3>
        <div className={styles.filterOptions}>
          <label>
            <input
              type="checkbox"
              checked={filterUsers}
              onChange={toggleFilterUsers}
            />
            Users
          </label>
          <label>
            <input
              type="checkbox"
              checked={filterWorkflows}
              onChange={toggleFilterWorkflows}
            />
            Workflows
          </label>
        </div>
      </div>

      {/* Right Column - Search Bar & Results */}
      <div className={styles.rightColumn}>
        <div className={styles.searchBar}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search for users or workflows..."
            value={query}
            onChange={handleSearch}
          />
        </div>

        {loading && <p className={styles.loadingText}>Searching...</p>}

        {!loading && (
          <>
            <p className={styles.resultCount}>
              {filteredUsers.length + filteredWorkflows.length} results found
            </p>

            {/* Users Section */}
            {filterUsers && filteredUsers.length > 0 && (
              <div className={styles.section}>
                <h3>
                  <FontAwesomeIcon icon={faUser} /> Users
                </h3>
                <ul>
                  {filteredUsers.map((user) => (
                    <li
                      key={user.id}
                      onClick={() => handleUserClick(user.id)}
                      className={styles.userItem}
                    >
                      <strong>
                        {user.first_name} {user.last_name}
                      </strong>{" "}
                      <span className={styles.username}>{user.user_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Workflows Section */}
            {filterWorkflows && filteredWorkflows.length > 0 && (
              <div className={styles.section}>
                <h3>
                  <FontAwesomeIcon icon={faProjectDiagram} /> Workflows
                </h3>
                <ul>
                  {filteredWorkflows.map((workflow) => (
                    <li
                      key={workflow.id}
                      onClick={() => handleWorkflowClick(workflow.id)}
                      className={styles.workflowItem}
                    >
                      <strong>{workflow.name}</strong>{" "}
                      <span className={styles.workflowType}>
                        {workflow.type}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No Results Message */}
            {filteredUsers.length === 0 &&
              filteredWorkflows.length === 0 &&
              query.trim() && (
                <p className={styles.noResults}>No results found.</p>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
